const formatBonusLines = (bonuses) => {
  const lines = [];
  if ((bonuses?.deposit_capacity_bonus || 0) > 0) lines.push(`+${bonuses.deposit_capacity_bonus} deposit capacity`);
  if ((bonuses?.deposit_restore_per_hour_bonus || 0) > 0) lines.push(`+${bonuses.deposit_restore_per_hour_bonus}/hr recovery`);
  if ((bonuses?.raw_ore_yield_bonus || 0) > 0) lines.push(`+${Math.round(bonuses.raw_ore_yield_bonus * 100)}% ore yield`);
  if ((bonuses?.great_yield_chance_bonus || 0) > 0) lines.push(`+${Number((bonuses.great_yield_chance_bonus * 100).toFixed(2))}% great yield`);
  if ((bonuses?.discovery_bonus || 0) > 0) lines.push(`+${Math.round(bonuses.discovery_bonus * 100)}% discovery`);
  return lines;
};

const formatDuration = (seconds) => {
  const safe = Math.max(0, Number(seconds || 0));
  const hours = Math.floor(safe / 3600);
  const mins = Math.floor((safe % 3600) / 60);
  const secs = safe % 60;
  if (hours > 0) return `${hours}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

export const MiningProjectsPanel = ({ smithing, projectBusyKey, projectClaimBusy, onStartProject, onClaimProject }) => {
  const projects = smithing?.projects || [];
  const summary = smithing?.project_summary || {};
  const activeProject = smithing?.active_project;
  const bonusLines = formatBonusLines(summary?.bonuses);

  return (
    <section data-testid="mining-projects-panel" className="border border-amber-900/30 bg-zinc-950/80 p-4 rounded-sm space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-cinzel text-base text-amber-300 uppercase tracking-wide">Long-Term Projects</h2>
          <p className="text-xs text-zinc-500 mt-1">Invest ore into one active project at a time to permanently improve your mining economy.</p>
        </div>
        <div data-testid="mining-projects-summary" className="text-right text-xs text-zinc-400">
          <div>{summary.completed_count || 0} completed</div>
          <div>{summary.unlocked_count || 0} unlocked</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        <div data-testid="mining-projects-specializations-count" className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Pickaxe specializations</div>
          <div className="font-cinzel text-xl text-zinc-100 mt-2">{summary.pickaxe_specializations_completed || 0}</div>
        </div>
        <div data-testid="mining-projects-improvements-count" className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Mine improvements</div>
          <div className="font-cinzel text-xl text-zinc-100 mt-2">{summary.mine_improvements_completed || 0}</div>
        </div>
        <div data-testid="mining-projects-active-count" className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Active project</div>
          <div className="font-cinzel text-lg text-zinc-100 mt-2">{activeProject ? activeProject.name : "None"}</div>
        </div>
        <div data-testid="mining-projects-bonus-card" className="border border-zinc-800 bg-zinc-900/60 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Permanent bonuses</div>
          {bonusLines.length ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {bonusLines.map((line) => (
                <span key={line} className="text-[10px] uppercase tracking-[0.12em] text-amber-300 border border-amber-900/30 bg-amber-950/10 px-2 py-1 rounded-sm">{line}</span>
              ))}
            </div>
          ) : (
            <div className="mt-2 text-xs text-zinc-500">No permanent mining bonuses yet.</div>
          )}
        </div>
      </div>

      {activeProject && (
        <div data-testid="mining-active-project-card" className="border border-amber-900/30 bg-amber-950/10 p-4 rounded-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-amber-300">Current project</div>
              <div className="font-cinzel text-base text-zinc-100 mt-2">{activeProject.name}</div>
              <div className="text-xs text-zinc-400 mt-1">{activeProject.effect_summary}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-2">Ends {new Date(activeProject.ends_at).toLocaleString()}</div>
            </div>
            {activeProject.claimable ? (
              <button
                data-testid="mining-project-claim-active"
                type="button"
                onClick={onClaimProject}
                disabled={projectClaimBusy}
                className="px-4 py-2 border border-amber-600 text-amber-300 hover:bg-amber-950/20 rounded-sm text-xs font-cinzel uppercase tracking-[0.18em] disabled:opacity-50"
              >
                {projectClaimBusy ? "Claiming" : "Claim Project"}
              </button>
            ) : (
              <div data-testid="mining-active-project-timer" className="text-xs text-zinc-300">
                {formatDuration(activeProject.time_remaining_seconds)} remaining
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid xl:grid-cols-2 gap-4">
        {projects.map((project) => {
          const oreCostText = Object.entries(project.ore_costs || {}).map(([rarity, amount]) => `${amount} ${rarity}`).join(" • ");
          const busy = projectBusyKey === project.id;
          return (
            <article key={project.id} data-testid={`mining-project-${project.id}`} className="border border-zinc-800 bg-zinc-900/60 p-4 rounded-sm space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-cinzel text-base text-zinc-100">{project.name}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mt-1">{project.category.replaceAll("_", " ")} · {project.duration_label}</div>
                </div>
                {project.completed && <div data-testid={`mining-project-completed-${project.id}`} className="text-xs uppercase tracking-[0.18em] text-amber-300">Completed</div>}
              </div>

              <div className="text-xs text-zinc-400">{project.description}</div>
              <div className="text-xs text-zinc-200">{project.effect_summary}</div>
              <div className="border border-zinc-800 bg-zinc-950/50 p-3 rounded-sm text-xs text-zinc-400">
                Ore investment: <span data-testid={`mining-project-cost-${project.id}`} className="text-zinc-100">{oreCostText}</span>
              </div>

              {!project.unlocked ? (
                <div data-testid={`mining-project-locked-${project.id}`} className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                  Unlock by claiming {project.unlock_discovery_name} milestone {project.unlock_milestone}
                </div>
              ) : project.completed ? (
                <div className="text-xs uppercase tracking-[0.18em] text-amber-300">Permanent bonus active</div>
              ) : project.active ? (
                project.claimable ? (
                  <button
                    data-testid={`mining-project-claim-${project.id}`}
                    type="button"
                    onClick={onClaimProject}
                    disabled={projectClaimBusy}
                    className="px-4 py-2 border border-amber-600 text-amber-300 hover:bg-amber-950/20 rounded-sm text-xs font-cinzel uppercase tracking-[0.18em] disabled:opacity-50"
                  >
                    {projectClaimBusy ? "Claiming" : "Claim Project"}
                  </button>
                ) : (
                  <div data-testid={`mining-project-running-${project.id}`} className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                    Project underway • {formatDuration(project.time_remaining_seconds)} remaining
                  </div>
                )
              ) : (
                <button
                  data-testid={`mining-project-start-${project.id}`}
                  type="button"
                  onClick={() => onStartProject(project)}
                  disabled={busy || !!activeProject}
                  className="px-4 py-2 border border-amber-600 text-amber-300 hover:bg-amber-950/20 rounded-sm text-xs font-cinzel uppercase tracking-[0.18em] disabled:opacity-50"
                >
                  {busy ? "Starting" : activeProject ? "One Project At A Time" : "Start Project"}
                </button>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};